import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus,
  FileText,
  Search,
  Eye,
  Edit,
  Trash2,
  Loader2,
  Calendar,
  Building,
  User,
  RotateCcw,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsList = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTrash, setShowTrash] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/acoustics/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  const loadTrash = async () => {
    setLoadingTrash(true);
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/acoustics/trash`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrashItems(response.data || []);
    } catch (error) {
      console.error('Error loading trash:', error);
      toast.error('Erreur lors du chargement de la corbeille');
    } finally {
      setLoadingTrash(false);
    }
  };

  const restoreFromTrash = async (itemId) => {
    try {
      const token = localStorage.getItem('tkale_token');
      await axios.post(`${API}/acoustics/trash/${itemId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rapport restauré !');
      loadTrash();
      loadReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
    }
  };

  const deleteReport = async (reportId, e) => {
    e.stopPropagation();
    if (!window.confirm('Déplacer ce rapport vers la corbeille ?\n\nVous pourrez le restaurer pendant 48h.')) return;
    
    try {
      const token = localStorage.getItem('tkale_token');
      await axios.delete(`${API}/acoustics/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(reports.filter(r => r.report_id !== reportId));
      toast.success('Rapport déplacé vers la corbeille (récupérable 48h)');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredReports = reports.filter(report => 
    report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.destinataire?.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.site?.nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Brouillon': return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      case 'En cours': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'Terminé': return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
      case 'Validé': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      default: return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-cyan-500/30 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/acoustics')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center">
                  <FileText className="w-6 h-6 mr-2 text-cyan-400" />
                  Mes Rapports
                </h1>
                <p className="text-sm text-slate-400">{reports.length} rapport(s)</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => navigate('/acoustics/report/new')}
                className="bg-cyan-600 hover:bg-cyan-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau rapport
              </Button>
              <Button 
                variant="outline"
                onClick={() => { setShowTrash(true); loadTrash(); }}
                className="border-slate-600 text-slate-400 hover:text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Corbeille
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Barre de recherche */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Rechercher par titre, référence, client ou site..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
            />
          </div>
        </div>

        {/* Liste des rapports */}
        {filteredReports.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl text-slate-400 mb-2">
              {searchTerm ? 'Aucun rapport trouvé' : 'Aucun rapport créé'}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchTerm ? 'Essayez avec d\'autres termes de recherche' : 'Commencez par créer votre premier rapport'}
            </p>
            {!searchTerm && (
              <Button 
                onClick={() => navigate('/acoustics/report/new')}
                className="bg-cyan-600 hover:bg-cyan-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer un rapport
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredReports.map((report, index) => (
              <motion.div
                key={report.report_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className="bg-slate-800/50 border-slate-700 hover:border-cyan-500/50 transition-all cursor-pointer"
                  onClick={() => navigate(`/acoustics/report/${report.report_id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 font-mono">
                            {report.reference}
                          </Badge>
                          <Badge className={getStatusColor(report.status)}>
                            {report.status}
                          </Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-3">
                          {report.title || 'Sans titre'}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div className="flex items-center text-slate-400">
                            <User className="w-4 h-4 mr-2 text-slate-500" />
                            <span>{report.destinataire?.nom || report.destinataire?.societe || 'Non renseigné'}</span>
                          </div>
                          <div className="flex items-center text-slate-400">
                            <Building className="w-4 h-4 mr-2 text-slate-500" />
                            <span>{report.site?.nom || 'Site non renseigné'}</span>
                          </div>
                          <div className="flex items-center text-slate-400">
                            <Calendar className="w-4 h-4 mr-2 text-slate-500" />
                            <span>
                              {report.periode?.debut 
                                ? new Date(report.periode.debut).toLocaleDateString('fr-FR')
                                : 'Dates non définies'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/acoustics/report/${report.report_id}`);
                          }}
                          className="text-slate-400 hover:text-cyan-400"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/acoustics/report/edit/${report.report_id}`);
                          }}
                          className="text-slate-400 hover:text-amber-400"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => deleteReport(report.report_id, e)}
                          className="text-slate-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Corbeille */}
      <Dialog open={showTrash} onOpenChange={setShowTrash}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-orange-400" />
              Corbeille
            </DialogTitle>
          </DialogHeader>
          
          {loadingTrash ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
          ) : trashItems.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Trash2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>La corbeille est vide</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {trashItems.map(item => (
                <div 
                  key={item.report_id}
                  className="flex items-center justify-between p-3 bg-slate-900 rounded border border-slate-700"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{item.title || 'Sans titre'}</p>
                    <p className="text-xs text-slate-400">
                      Réf: {item.reference || '-'} • Supprimé le {new Date(item.deleted_at).toLocaleString('fr-FR')}
                    </p>
                    <p className="text-xs text-orange-400 flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3" />
                      {item.hours_remaining}h restantes pour restaurer
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => restoreFromTrash(item.report_id)}
                    className="bg-cyan-600 hover:bg-cyan-500 ml-3"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Restaurer
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsList;
